import { create } from 'zustand'
import { useShallow } from 'zustand/react/shallow'
import type { GenerationStyle } from '@backend/lib/ai/prompts/personaPrompt'
import type { DesignAspectRatioChoice } from '@/components/home/generation-style-popover'

/**
 * Hand-off between the home page and the workspace chat: the home page creates
 * the workspace, uploads any attached files, and parks the generation request
 * here; the workspace chat picks it up and sends it as the first agent message.
 *
 * In-memory only — a reload mid-generation drops the request (same fragility
 * as the previous home-page closure orchestration).
 */

export type PendingGenerationFile = {
  key: string
  url: string
  name: string
  type: string
  size: number
}

export type PendingGeneration = {
  workspaceId: number
  prompt: string
  contextURLs: string[]
  /** Uploaded S3 assets. Only complete once `status` is 'ready'. */
  files: PendingGenerationFile[]
  moodboardId: number | null
  style: GenerationStyle
  /** 'auto' keeps the generator's per-topic ratio; an explicit value overrides every generated design. */
  designAspectRatio: DesignAspectRatioChoice
  /** uploading → ready (chat sends it) → sent (in-flight, kept for file metadata until cleared) */
  status: 'uploading' | 'ready' | 'sent'
}

type Store = {
  pending: PendingGeneration | null
  setPending: (pending: PendingGeneration) => void
  updatePending: (workspaceId: number, partial: Partial<PendingGeneration>) => void
  clearPending: (workspaceId: number) => void
}

export const pendingGenerationStore = create<Store>((set) => ({
  pending: null,

  setPending: (pending) => set({ pending }),

  updatePending: (workspaceId, partial) =>
    set((state) =>
      state.pending?.workspaceId === workspaceId
        ? { pending: { ...state.pending, ...partial } }
        : state,
    ),

  clearPending: (workspaceId) =>
    set((state) => (state.pending?.workspaceId === workspaceId ? { pending: null } : state)),
}))

export const usePendingGenerationForWorkspace = (workspaceId: number): PendingGeneration | null => {
  return pendingGenerationStore(
    useShallow((s) => (s.pending?.workspaceId === workspaceId ? s.pending : null)),
  )
}
